import { useRef, useState, useEffect, useCallback } from 'react'

// Horizontally scrollable wrapper with two overflow cues:
//   1. edge fades (+ a chevron on the right) on whichever side has hidden content
//   2. an always-visible scroll-position bar pinned to the bottom of the container,
//      whose thumb width/position reflect how much is visible and where you are.
// All cues hide automatically when the content fits (nothing to scroll).
export default function HScroll({ children, className = '' }) {
  const ref = useRef(null)
  const [m, setM] = useState({ scrollLeft: 0, scrollWidth: 1, clientWidth: 1 })

  const update = useCallback(() => {
    const el = ref.current
    if (!el) return
    setM(prev => {
      // Bail if nothing changed — returning the same reference skips the re-render
      // and prevents the every-render measure effect from looping forever.
      if (
        prev.scrollLeft === el.scrollLeft &&
        prev.scrollWidth === el.scrollWidth &&
        prev.clientWidth === el.clientWidth
      ) {
        return prev
      }
      return { scrollLeft: el.scrollLeft, scrollWidth: el.scrollWidth, clientWidth: el.clientWidth }
    })
  }, [])

  // Re-measure on every render so content changes (toggling columns, loading rows)
  // are picked up, not just scroll/resize events.
  useEffect(update)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(el)
    window.addEventListener('resize', update)
    return () => {
      el.removeEventListener('scroll', update)
      observer.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [update])

  const maxScroll = m.scrollWidth - m.clientWidth
  const overflow = maxScroll > 1
  const canLeft = m.scrollLeft > 1
  const canRight = m.scrollLeft < maxScroll - 1
  // Thumb is at least 12% wide so it stays visible on very wide tables; it travels
  // from 0 to (100 - thumbWidth)% as the content scrolls from 0 to its max.
  const thumbWidth = Math.max(12, (m.clientWidth / m.scrollWidth) * 100)
  const thumbLeft = overflow ? (m.scrollLeft / maxScroll) * (100 - thumbWidth) : 0

  return (
    <div className="relative">
      <div ref={ref} className={`overflow-x-auto ${className}`}>
        {children}
      </div>

      {canLeft && (
        <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-base-100 to-transparent" />
      )}

      {canRight && (
        <>
          <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-base-100 to-transparent" />
          <div className="pointer-events-none absolute top-1/2 right-1.5 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full bg-base-100/90 shadow ring-1 ring-base-300">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </>
      )}

      {overflow && (
        <div className="pointer-events-none absolute inset-x-2 bottom-1 h-1.5 rounded-full bg-base-content/10">
          <div
            className="absolute top-0 h-1.5 rounded-full bg-base-content/40"
            style={{ width: `${thumbWidth}%`, left: `${thumbLeft}%` }}
          />
        </div>
      )}
    </div>
  )
}
